import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageShell } from '../../layout/Shell';
import { listMaps, listPlayers } from '../../shared/api/client';
import type { ApiMapSummary, ApiPlayerSummary } from '../../shared/api/types';
import { Icon } from '../../shared/design/Icons';
import { Button, EmptyState, TierBadge } from '../../shared/design/Primitives';
import { useAsync } from '../../shared/hooks/useAsync';
import { formatTime, shortMapName } from '../../shared/model/Replay';

type Tab = 'maps' | 'players';

export function LeaderboardPage() {
  const [search, setSearch] = useSearchParams();
  const tab: Tab = search.get('tab') === 'players' ? 'players' : 'maps';
  const setTab = (t: Tab) => {
    if (t === 'maps') {
      search.delete('tab');
    } else {
      search.set('tab', t);
    }
    setSearch(search, { replace: true });
  };

  return (
    <PageShell active="leaderboard">
      <div className="tm" style={{ maxWidth: 1320, margin: '0 auto', padding: '28px 24px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
          <h1 className="h1">Leaderboard</h1>
        </div>
        <p className="p" style={{ marginBottom: 20, maxWidth: 720 }}>
          {tab === 'maps'
            ? 'Maps the community has scanned, ranked by activity. Click a map to see its leaderboard.'
            : 'Players ranked by how many replays we have on file for them.'}
        </p>

        <Tabs tab={tab} setTab={setTab} />

        {tab === 'maps' ? <MapsView /> : <PlayersView />}
      </div>
    </PageShell>
  );
}

// ============ Tabs ============
function Tabs({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  const items: { id: Tab; label: string }[] = [
    { id: 'maps', label: 'Maps' },
    { id: 'players', label: 'Players' },
  ];
  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        marginBottom: 20,
        borderBottom: '1px solid var(--border)',
      }}
    >
      {items.map((it) => {
        const active = it.id === tab;
        return (
          <button
            key={it.id}
            onClick={() => setTab(it.id)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '10px 14px',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active ? 'var(--fg-1)' : 'var(--fg-3)',
              borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
              marginBottom: -1,
              fontFamily: 'var(--font-sans)',
            }}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// ============ Maps view ============
function MapsView() {
  const navigate = useNavigate();
  const { data, loading, error } = useAsync(() => listMaps(), []);

  if (loading) return <TableSkeleton />;
  if (error) return <EmptyState title="Couldn't load maps" body={error.message} icon={<Icon.Warn />} accent="var(--bad)" />;
  const maps = data ?? [];
  if (!maps.length) {
    return (
      <EmptyState
        title="No maps scanned yet"
        body="Scan or bulk-import some replays first. Maps will appear here as soon as we have any."
        icon={<Icon.Leaderboard />}
        cta={<Button variant="primary" icon={<Icon.Scan />} onClick={() => navigate('/')}>Scan a replay</Button> as any}
      />
    );
  }

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        overflow: 'hidden',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--bg-surface-2)' }}>
            {[
              ['Map', 'left'],
              ['Replays', 'right'],
              ['Fastest', 'right'],
              ['By', 'left'],
              ['Tier', 'left'],
              ['Sus.', 'right'],
              ['Latest', 'left'],
              ['', 'right'],
            ].map(([h, align], i) => (
              <th
                key={i}
                style={{
                  padding: '11px 14px',
                  textAlign: align as 'left' | 'right',
                  color: 'var(--fg-3)',
                  fontWeight: 500,
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {maps.map((m, i) => (
            <MapRow key={m.challengeUid} m={m} alt={i % 2 === 1} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MapRow({ m, alt }: { m: ApiMapSummary; alt: boolean }) {
  const navigate = useNavigate();
  const open = () => navigate(`/leaderboard/map/${encodeURIComponent(m.challengeUid)}`);
  return (
    <tr
      onClick={open}
      style={{
        borderTop: '1px solid var(--border-subtle)',
        background: alt ? 'rgba(255,255,255,0.012)' : 'transparent',
        cursor: 'pointer',
      }}
    >
      <td style={{ padding: '12px 14px', color: 'var(--fg-1)', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
        {shortMapName(m.map || m.challengeUid)}
      </td>
      <td
        style={{
          padding: '12px 14px',
          textAlign: 'right',
          fontFamily: 'var(--font-mono)',
          color: 'var(--fg-1)',
          fontWeight: 600,
        }}
      >
        {m.replayCount}
      </td>
      <td
        style={{
          padding: '12px 14px',
          textAlign: 'right',
          fontFamily: 'var(--font-mono)',
          color: 'var(--fg-1)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatTime(m.fastestTimeMS)}
      </td>
      <td style={{ padding: '12px 14px', color: 'var(--fg-2)' }}>{m.fastestAuthor || '—'}</td>
      <td style={{ padding: '12px 14px' }}>
        {m.fastestTier ? <TierBadge tier={m.fastestTier} size="sm" label={false} /> : <span style={{ color: 'var(--fg-muted)' }}>—</span>}
      </td>
      <td
        style={{
          padding: '12px 14px',
          textAlign: 'right',
          fontFamily: 'var(--font-mono)',
          color: m.suspiciousReplayCount > 0 ? 'var(--warn)' : 'var(--fg-muted)',
        }}
      >
        {m.suspiciousReplayCount === 0 ? '—' : `${m.suspiciousReplayCount}/${m.replayCount}`}
      </td>
      <td
        style={{
          padding: '12px 14px',
          color: 'var(--fg-3)',
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
        }}
      >
        {m.latestScan ? m.latestScan.slice(0, 10) : ''}
      </td>
      <td style={{ padding: '12px 14px', textAlign: 'right' }}>
        <span style={{ color: 'var(--fg-3)' }}>→</span>
      </td>
    </tr>
  );
}

// ============ Players view ============
function PlayersView() {
  // "Only with multiple replays" is on by default — that's the bucket the
  // user actually cares about; one-shot scans dominate the raw list.
  const [multipleOnly, setMultipleOnly] = useState(true);
  const { data, loading, error } = useAsync(() => listPlayers(multipleOnly ? 2 : 1), [multipleOnly]);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 14,
        }}
      >
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: 'var(--fg-2)',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={multipleOnly}
            onChange={(e) => setMultipleOnly(e.target.checked)}
            style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
          />
          Only players with 2+ replays
        </label>
      </div>

      {loading ? (
        <TableSkeleton />
      ) : error ? (
        <EmptyState title="Couldn't load players" body={error.message} icon={<Icon.Warn />} accent="var(--bad)" />
      ) : (data?.length ?? 0) === 0 ? (
        <EmptyState
          title={multipleOnly ? 'No players with multiple replays yet' : 'No players yet'}
          body={multipleOnly ? 'Toggle off the filter to see one-shot players, or import more replays.' : 'Scan some replays first.'}
          icon={<Icon.About />}
        />
      ) : (
        <PlayersTable players={data!} />
      )}
    </div>
  );
}

function PlayersTable({ players }: { players: ApiPlayerSummary[] }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        overflow: 'hidden',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--bg-surface-2)' }}>
            {[
              ['Player', 'left'],
              ['Replays', 'right'],
              ['Maps', 'right'],
              ['Best tier', 'left'],
              ['Sus. runs', 'right'],
              ['Sus. clusters', 'right'],
              ['Latest', 'left'],
            ].map(([h, align], i) => (
              <th
                key={i}
                style={{
                  padding: '11px 14px',
                  textAlign: align as 'left' | 'right',
                  color: 'var(--fg-3)',
                  fontWeight: 500,
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {players.map((p, i) => (
            <tr
              key={p.authorName + i}
              style={{
                borderTop: '1px solid var(--border-subtle)',
                background: i % 2 === 1 ? 'rgba(255,255,255,0.012)' : 'transparent',
              }}
            >
              <td style={{ padding: '12px 14px', color: 'var(--fg-1)', fontWeight: 500 }}>{p.authorName || '—'}</td>
              <td
                style={{
                  padding: '12px 14px',
                  textAlign: 'right',
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--fg-1)',
                  fontWeight: 600,
                }}
              >
                {p.replayCount}
              </td>
              <td
                style={{
                  padding: '12px 14px',
                  textAlign: 'right',
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--fg-2)',
                }}
              >
                {p.mapCount}
              </td>
              <td style={{ padding: '12px 14px' }}>
                <TierBadge tier={p.bestTier} size="sm" label={false} />
              </td>
              <td
                style={{
                  padding: '12px 14px',
                  textAlign: 'right',
                  fontFamily: 'var(--font-mono)',
                  color: p.suspiciousReplayCount > 0 ? 'var(--warn)' : 'var(--fg-muted)',
                }}
              >
                {p.suspiciousReplayCount === 0 ? '—' : `${p.suspiciousReplayCount}/${p.replayCount}`}
              </td>
              <td
                style={{
                  padding: '12px 14px',
                  textAlign: 'right',
                  fontFamily: 'var(--font-mono)',
                  color: p.totalSuspiciousClusters > 0 ? 'var(--bad)' : 'var(--fg-muted)',
                }}
              >
                {p.totalSuspiciousClusters === 0 ? '—' : `×${p.totalSuspiciousClusters}`}
              </td>
              <td
                style={{
                  padding: '12px 14px',
                  color: 'var(--fg-3)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                }}
              >
                {p.latestScan ? p.latestScan.slice(0, 10) : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div
      style={{
        height: 240,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
      }}
    />
  );
}
